import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Edit3, MapPin, Briefcase, Globe, Linkedin, UserPlus, MessageCircle, Check, Camera, Loader2, Plus, Trash2, Music } from "lucide-react";
import { toast } from "sonner";

const Avatar = ({ profile, fullName, size = 128 }) => (
    <div className="rounded-full border-4 border-paper bg-surface overflow-hidden soft-shadow" style={{ width: size, height: size }}>
        {profile?.avatar_url ? (
            <img src={mediaUrl(profile.avatar_url)} alt="" className="w-full h-full object-cover" />
        ) : (
            <div className="w-full h-full flex items-center justify-center font-heading text-4xl text-muted">{fullName?.[0]?.toUpperCase()}</div>
        )}
    </div>
);

// Mostra solo link http(s): evita link "javascript:" salvati nei profili
const safeHref = (url) => (/^https?:\/\//i.test(url || "") ? url : `https://${(url || "").replace(/^[a-z][a-z0-9+.-]*:/i, "")}`);
// Profili LinkedIn salvati come solo "nome-cognome" (versioni precedenti) diventano un link valido
const linkedinHref = (v) => (/linkedin\./i.test(v || "") ? safeHref(v) : `https://www.linkedin.com/in/${encodeURIComponent((v || "").trim().replace(/^@/, ""))}`);

const spotifyEmbed = (url) => {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes("spotify.com")) {
            const path = u.pathname.replace(/^\//, "").replace(/^embed\//, "");
            return `https://open.spotify.com/embed/${path}`;
        }
    } catch {}
    return null;
};

const Experiences = ({ profile, isMe, onChange }) => {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ title: "", company: "", location: "", start_date: "", end_date: "", description: "" });
    const list = profile?.experiences || [];

    const submit = async () => {
        if (!form.title || !form.company || !form.start_date) { toast.error("Compila titolo, azienda e data inizio"); return; }
        try {
            await api.post("/profile/experiences", form);
            setForm({ title: "", company: "", location: "", start_date: "", end_date: "", description: "" });
            setAdding(false);
            onChange();
            toast.success("Esperienza aggiunta");
        } catch { toast.error("Errore"); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/profile/experiences/${id}`);
            onChange();
        } catch {}
    };

    return (
        <section>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs uppercase tracking-widest text-soft">Esperienze</h2>
                {isMe && !adding && <button data-testid="exp-add" onClick={() => setAdding(true)} className="btn-ghost text-xs"><Plus size={14} /> Aggiungi</button>}
            </div>
            {adding && (
                <div className="bg-surface rounded-xl p-4 mb-4 space-y-2">
                    <div className="grid sm:grid-cols-2 gap-2">
                        <input placeholder="Ruolo" data-testid="exp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-clean" />
                        <input placeholder="Azienda" data-testid="exp-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-clean" />
                        <input placeholder="Località" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-clean" />
                        <div className="grid grid-cols-2 gap-2">
                            <input placeholder="Inizio (es. 2020-01)" data-testid="exp-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-clean" />
                            <input placeholder="Fine (vuoto = oggi)" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-clean" />
                        </div>
                    </div>
                    <textarea placeholder="Descrizione (opzionale)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-clean min-h-[60px]" />
                    <div className="flex gap-2">
                        <button onClick={submit} data-testid="exp-save" className="btn-primary text-sm">Salva</button>
                        <button onClick={() => setAdding(false)} className="btn-ghost text-sm">Annulla</button>
                    </div>
                </div>
            )}
            {list.length === 0 && !adding && <div className="text-sm text-muted">Nessuna esperienza ancora.</div>}
            <div className="space-y-3">
                {list.map((e) => (
                    <div key={e.id} className="bg-surface/60 rounded-xl p-4 flex items-start gap-3" data-testid={`exp-item-${e.id}`}>
                        <div className="w-10 h-10 rounded-full bg-paper border border-border-line flex items-center justify-center shrink-0">
                            <Briefcase size={16} className="text-bordeaux" />
                        </div>
                        <div className="flex-1">
                            <div className="font-medium text-ink">{e.title}</div>
                            <div className="text-sm text-muted">{e.company}{e.location && ` · ${e.location}`}</div>
                            <div className="text-xs text-soft mt-1">{e.start_date} → {e.end_date || "oggi"}</div>
                            {e.description && <p className="text-sm text-ink mt-2 whitespace-pre-wrap">{e.description}</p>}
                        </div>
                        {isMe && (
                            <button onClick={() => remove(e.id)} className="text-soft hover:text-bordeaux transition-colors p-1"><Trash2 size={14} /></button>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

const Gallery = ({ profile, isMe, onChange }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const list = profile?.gallery || [];

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post("/profile/gallery", { url: data.url, media_type: f.type.startsWith("video") ? "video" : "image", caption: "" });
            onChange();
            toast.success("Aggiunto alla galleria");
        } catch { toast.error("Upload fallito"); }
        finally { setUploading(false); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/profile/gallery/${id}`);
            onChange();
        } catch {}
    };

    return (
        <section>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs uppercase tracking-widest text-soft">Galleria</h2>
                {isMe && (
                    <>
                        <button data-testid="gal-add" onClick={() => fileRef.current?.click()} className="btn-ghost text-xs">
                            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Aggiungi
                        </button>
                        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={upload} />
                    </>
                )}
            </div>
            {list.length === 0 && <div className="text-sm text-muted">Nessun contenuto ancora.</div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {list.map((g) => (
                    <div key={g.id} className="relative group rounded-xl overflow-hidden bg-surface aspect-square">
                        {g.media_type === "video" ? (
                            <video src={mediaUrl(g.url)} controls className="w-full h-full object-cover" />
                        ) : (
                            <img src={mediaUrl(g.url)} alt="" className="w-full h-full object-cover" />
                        )}
                        {isMe && (
                            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

const Profile = () => {
    const { user_id } = useParams();
    const { user: me, refresh } = useAuth();
    const nav = useNavigate();
    const [profile, setProfile] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const avatarRef = useRef(null);
    const coverRef = useRef(null);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);

    const isMe = me?.id === user_id;

    const load = async () => {
        try {
            const { data } = await api.get(`/profile/${user_id}`);
            setProfile(data);
            setForm({
                full_name: data.full_name,
                headline: data.profile?.headline || "",
                industry: data.profile?.industry || "",
                location: data.profile?.location || "",
                bio: data.profile?.bio || "",
                skills: (data.profile?.skills || []).join(", "),
                website: data.profile?.website || "",
                linkedin: data.profile?.linkedin || "",
                spotify_episode_url: data.profile?.spotify_episode_url || "",
            });
        } catch (e) {
            toast.error("Profilo non trovato");
            nav("/feed");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user_id]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/profile", {
                ...form,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
            });
            await load();
            await refresh();
            setEditing(false);
            toast.success("Profilo aggiornato");
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
        finally { setSaving(false); }
    };

    const uploadImage = async (file, kind) => {
        const fd = new FormData();
        fd.append("file", file);
        if (kind === "avatar") setUploadingAvatar(true); else setUploadingCover(true);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/profile", { [kind === "avatar" ? "avatar_url" : "cover_url"]: data.url });
            await load();
            await refresh();
        } catch { toast.error("Upload fallito"); }
        finally {
            if (kind === "avatar") setUploadingAvatar(false); else setUploadingCover(false);
        }
    };

    const requestConnect = async () => {
        try { await api.post(`/connections/request/${user_id}`); toast.success("Richiesta inviata"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const acceptConnect = async () => {
        if (!profile.connection) return;
        try { await api.post(`/connections/${profile.connection.id}/accept`); toast.success("Collegamento accettato"); load(); }
        catch { toast.error("Errore"); }
    };
    const startChat = () => nav(`/messages?to=${user_id}`);

    if (!profile) return <div className="text-center py-20 text-muted">Caricamento…</div>;

    const conn = profile.connection;
    const connState = conn?.status === "accepted" ? "connected" : conn?.status === "pending" ? (conn.to_user === me?.id ? "incoming" : "pending") : "none";
    const spotifyUrl = spotifyEmbed(profile.profile?.spotify_episode_url);

    return (
        <div className="max-w-4xl mx-auto" data-testid="profile-page">
            <div className="bg-paper rounded-3xl border border-border-line soft-shadow-sm overflow-hidden">
                {/* Cover */}
                <div className="relative h-48 md:h-60 bg-gradient-to-br from-surface to-bg">
                    {profile.profile?.cover_url && <img src={mediaUrl(profile.profile.cover_url)} alt="" className="w-full h-full object-cover" />}
                    {isMe && (
                        <button data-testid="cover-upload" onClick={() => coverRef.current?.click()} className="absolute bottom-3 right-3 btn-ghost text-xs bg-paper/90 backdrop-blur">
                            {uploadingCover ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Cambia copertina
                        </button>
                    )}
                    <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "cover")} />
                </div>

                {/* Avatar — absolute, overlapping cover */}
                <div className="relative px-6 md:px-10">
                    <div className="absolute -top-16 left-6 md:left-10 z-10">
                        <div className="relative">
                            <Avatar profile={profile.profile} fullName={profile.full_name} />
                            {isMe && (
                                <button data-testid="avatar-upload" onClick={() => avatarRef.current?.click()} className="absolute bottom-1 right-1 bg-bordeaux text-white rounded-full p-1.5 soft-shadow hover:bg-bordeaux-hover">
                                    {uploadingAvatar ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                                </button>
                            )}
                            <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "avatar")} />
                        </div>
                    </div>
                </div>

                {/* Info section — below cover, with top padding to clear avatar bulge */}
                <div className="px-6 md:px-10 pt-20 md:pt-6 pb-8">
                    <div className="flex flex-col md:flex-row md:items-start md:gap-6 md:pl-40">
                        <div className="flex-1 min-w-0">
                            <h1 className="font-heading text-3xl md:text-4xl text-ink break-words leading-tight" data-testid="profile-name">{profile.full_name}</h1>
                            {profile.profile?.headline && <p className="text-muted text-sm mt-1.5 break-words">{profile.profile.headline}</p>}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-xs text-soft">
                                {profile.profile?.industry && <span className="flex items-center gap-1"><Briefcase size={12} /> {profile.profile.industry}</span>}
                                {profile.profile?.location && <span className="flex items-center gap-1"><MapPin size={12} /> {profile.profile.location}</span>}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4 md:mt-0 shrink-0">
                            {isMe ? (
                                <button data-testid="edit-profile" onClick={() => setEditing(!editing)} className="btn-outline text-sm"><Edit3 size={14} /> {editing ? "Annulla" : "Modifica"}</button>
                            ) : (
                                <>
                                    {connState === "none" && <button data-testid="connect-btn" onClick={requestConnect} className="btn-primary text-sm"><UserPlus size={14} /> Collegati</button>}
                                    {connState === "pending" && <button disabled className="btn-outline text-sm opacity-60">In attesa</button>}
                                    {connState === "incoming" && <button onClick={acceptConnect} className="btn-primary text-sm"><Check size={14} /> Accetta</button>}
                                    {connState === "connected" && <span className="btn-outline text-sm opacity-60"><Check size={14} /> Collegati</span>}
                                    <button data-testid="message-btn" onClick={startChat} className="btn-outline text-sm"><MessageCircle size={14} /> Messaggio</button>
                                </>
                            )}
                        </div>
                    </div>

                    {editing ? (
                        <div className="mt-8 space-y-4 fade-up">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input placeholder="Nome" data-testid="ep-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-clean" />
                                <input placeholder="Headline" data-testid="ep-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="input-clean" />
                                <input placeholder="Settore" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="input-clean" />
                                <input placeholder="Località" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-clean" />
                                <input placeholder="Sito web (es. www.tuosito.it)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-clean" />
                                <input placeholder="Profilo LinkedIn (es. linkedin.com/in/nome-cognome)" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="input-clean" />
                            </div>
                            <textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="input-clean min-h-[120px]" />
                            <input placeholder="Competenze (separa con virgola)" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="input-clean" />
                            <div>
                                <label className="text-xs uppercase tracking-widest text-soft flex items-center gap-1"><Music size={12} /> Link alla tua intervista (Spotify) — facoltativo</label>
                                <input data-testid="ep-spotify" placeholder="https://open.spotify.com/episode/..." value={form.spotify_episode_url} onChange={(e) => setForm({ ...form, spotify_episode_url: e.target.value })} className="input-clean mt-1" />
                                <div className="text-[11px] text-soft mt-1">Incolla l'URL di un episodio Spotify: comparirà come player sul tuo profilo pubblico.</div>
                            </div>
                            <button data-testid="save-profile" onClick={save} disabled={saving} className="btn-primary">{saving ? "Salvataggio…" : "Salva"}</button>
                        </div>
                    ) : (
                        <div className="mt-8 space-y-8">
                            {profile.profile?.bio && (
                                <section>
                                    <h2 className="text-xs uppercase tracking-widest text-soft mb-2">Bio</h2>
                                    <p className="text-ink whitespace-pre-wrap leading-relaxed">{profile.profile.bio}</p>
                                </section>
                            )}
                            {profile.profile?.skills?.length > 0 && (
                                <section>
                                    <h2 className="text-xs uppercase tracking-widest text-soft mb-3">Competenze</h2>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.profile.skills.map((s) => (
                                            <span key={s} className="px-3 py-1 rounded-full bg-surface border border-border-line text-sm">{s}</span>
                                        ))}
                                    </div>
                                </section>
                            )}
                            <hr className="gold-divider" />
                            <Experiences profile={profile.profile} isMe={isMe} onChange={load} />
                            <hr className="gold-divider" />
                            <Gallery profile={profile.profile} isMe={isMe} onChange={load} />
                            {spotifyUrl && (
                                <>
                                    <hr className="gold-divider" />
                                    <section>
                                        <h2 className="text-xs uppercase tracking-widest text-soft mb-3 flex items-center gap-1"><Music size={12} /> La sua intervista su Spotify</h2>
                                        <iframe data-testid="spotify-episode" title="Spotify episode" src={spotifyUrl} width="100%" height="232" frameBorder="0" allowtransparency="true" allow="encrypted-media" className="rounded-xl" />
                                    </section>
                                </>
                            )}
                            {(profile.profile?.website || profile.profile?.linkedin) && (
                                <>
                                    <hr className="gold-divider" />
                                    <section className="flex gap-3 text-sm">
                                        {profile.profile?.website && <a target="_blank" rel="noreferrer" href={safeHref(profile.profile.website)} className="text-bordeaux hover:underline flex items-center gap-1"><Globe size={14} /> Sito</a>}
                                        {profile.profile?.linkedin && <a target="_blank" rel="noreferrer" href={linkedinHref(profile.profile.linkedin)} className="text-bordeaux hover:underline flex items-center gap-1"><Linkedin size={14} /> LinkedIn</a>}
                                    </section>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;
